import { useState } from "react";
import { useUser } from "../../../../../business-types/grocery/components/UserContext";
import api from "../../../../../features/auth/axiosConfig";
import { notifyError, notifySuccess } from "../../../../../assets/toastService";
import { GetConfigurationPageSetup, getUser } from "../../../../services/services";
import { useBusinessNavigation } from "./useBusinessNavigation";
import { useThemeSetup } from "./useThemeSetup";

export interface SubscriptionStatusResponse {
  expiry_date?: string;
  grace_end_date?: string;
  message?: string;
  status: "active" | "inactive" | "expired" | "grace_period" | "blocked";
  warning?: string | null;
  daysRemaining?: number;
}

export const useAuth = (user_name: string, password: string, setIsLoginDisabled: (val: boolean) => void, setemployee: (val: boolean) => void) => {
  const [Loading, setLoading] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);

  const { setUser, setConfigurations, setSubscriptionInfo } = useUser();
  const { navigateByBusiness } = useBusinessNavigation();
  const { setupTheme } = useThemeSetup();

  const handleLogin = async () => {
    setLoading(true);
    setIsLoginDisabled(true);
    setSubscriptionError(null);

    try {
      const formData = new FormData();
      formData.append("user_name", user_name);
      formData.append("password", password);

      // LOGIN API
      const response = await api.post(`/pos/login`, formData);

      if (response.status !== 200) {
        throw new Error("Login failed");
      }

      const { token, user } = response.data;

      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      const subscriptionRes = await api.get("/app/v1/subscription");
      const subscription = subscriptionRes.data as SubscriptionStatusResponse;

      if (subscription.status === "blocked") {
        delete api.defaults.headers.common["Authorization"];

        setSubscriptionError(subscription.message || "Your subscription has expired. Please contact POS support.");

        setIsLoginDisabled(false);
        setLoading(false);
        return;
      }

      sessionStorage.setItem("jsonwebtoken", token);

      setSubscriptionInfo(subscription);

      await handleSuccessfulLogin(user);
    } catch (error: any) {
      console.error("Login error:", error);

      if (error.response) {
        const status = error.response.status;
        const backendMessage = error.response.data?.message;

        if (status === 401) {
          notifyError("Invalid username or password.");
        } else {
          setSubscriptionError(backendMessage || "Subscription error. Please contact support.");
        }
      } else if (error.request) {
        notifyError("No response from server. Check internet connection.");
      } else {
        notifyError("Unexpected login error.");
      }

      setIsLoginDisabled(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessfulLogin = async (user: any) => {
    try {
      const userRes = await getUser();

      setUser(userRes?.data?.user_details || null);

      if (userRes?.data?.user_details?.organization_name) {
        setupTheme(userRes.data.user_details.organization_name);
      }

      const configRes = await GetConfigurationPageSetup();

      if (configRes?.data?.result?.[0]) {
        setConfigurations(configRes.data.result[0]);
      }

      const business_type = user?.[0]?.business_type;

      sessionStorage.setItem("business_type", business_type);

      notifySuccess("Logged in successfully");

      setTimeout(() => {
        navigateByBusiness(business_type, "");
      }, 1000);
    } catch {
      notifyError("Error loading user data.");
    }
  };

  return {
    handleLogin,
    Loading,
    subscriptionError,
  };
};
